import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { readingService } from '../services/reading';
import { ReadingArticle, ReadingStats } from '../types';

const Reading: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<ReadingArticle[]>([]);
  const [stats, setStats] = useState<ReadingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  
  // Form state
  const [difficulty, setDifficulty] = useState(user?.current_level || 'A2');
  const [category, setCategory] = useState('');
  const [topic, setTopic] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [articlesData, statsData] = await Promise.all([
        readingService.getArticles(),
        readingService.getStats()
      ]);
      setArticles(articlesData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setGenerating(true);
    
    try {
      const newArticle = await readingService.generateArticle({
        difficulty_level: difficulty,
        category: category || undefined,
        topic: topic || undefined
      });
      
      setShowGenerateForm(false);
      setCategory('');
      setTopic('');
      await loadData();
      
      // Navigate to the new article
      navigate(`/reading/${newArticle.id}`);
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Error generating article');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-indigo-600 hover:text-indigo-800"
            >
              ← Back
            </button>
            <h1 className="text-2xl font-bold text-indigo-600">Reading Practice</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-700">Points: {user?.total_points}</span>
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-4">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-4xl mb-2">📚</div>
              <p className="text-gray-600">Articles Read</p>
              <p className="text-3xl font-bold text-indigo-600">{stats.total_articles_read}</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-4xl mb-2">✅</div>
              <p className="text-gray-600">Questions Answered</p>
              <p className="text-3xl font-bold text-green-600">{stats.total_questions_answered}</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-4xl mb-2">🎯</div>
              <p className="text-gray-600">Accuracy</p>
              <p className="text-3xl font-bold text-purple-600">{stats.accuracy_rate}%</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-4xl mb-2">⏱️</div>
              <p className="text-gray-600">Avg. Time</p>
              <p className="text-3xl font-bold text-orange-600">{Math.floor(stats.average_time_per_article / 60)}m</p>
            </div>
          </div>
        )}

        {/* Generate Button */}
        <div className="mb-6">
          <button
            onClick={() => setShowGenerateForm(!showGenerateForm)}
            className="bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 shadow-lg"
          >
            ✨ Generate New Article
          </button>
        </div>

        {/* Generate Form */}
        {showGenerateForm && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Generate New Article</h2>
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Difficulty Level
                  </label>
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="A1">A1 - Beginner</option>
                    <option value="A2">A2 - Elementary</option>
                    <option value="B1">B1 - Intermediate</option>
                    <option value="B2">B2 - Upper Intermediate</option>
                    <option value="C1">C1 - Advanced</option>
                    <option value="C2">C2 - Proficient</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Category (optional)
                  </label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="news, story, travel, science..."
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Topic (optional)
                  </label>
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="artificial intelligence, cooking..."
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={generating}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
                >
                  {generating ? 'Generating...' : 'Generate Article'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowGenerateForm(false)}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Articles List */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4">Available Articles ({articles.length})</h2>
          
          {articles.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              No articles yet. Generate your first article above!
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map((article) => (
                <div
                  key={article.id}
                  onClick={() => navigate(`/reading/${article.id}`)}
                  className="border rounded-lg p-4 hover:shadow-lg transition cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {article.difficulty_level}
                    </span>
                    {article.category && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                        {article.category}
                      </span>
                    )}
                  </div>
                  
                  <h3 className="font-bold text-lg mb-2">{article.title}</h3>
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>📝 {article.word_count} words</p>
                    <p>⏱️ ~{article.estimated_time_minutes} min read</p>
                    <p>❓ {article.questions?.length || 5} questions</p>
                  </div>
                  
                  <button className="mt-4 w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700">
                    Start Reading →
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reading;
